import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Alert, AlertTitle, Collapse } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useAppDispatch } from '../../../../pages/assessment';
import {
  selectMedicalInfoAnswers,
  selectPatientGuardians,
  updatePrimaryPolicyholder,
} from '../../../../redux/assessment';
import { PrimaryPolicyHolderForm } from './form';
import {
  AdultPolicyholderOptions,
  MinorPolicyHolderRelationLabelValueMap,
  PolicyHolderRelationLabelValueMap,
  createPolicyholderObjectHelper,
  minorPolicyholderOptions,
} from '../../../../helpers/assessment';
import { selectIsGuardian } from '../../../../redux/session';
import {
  AssessmentButtons,
  AssessmentCard,
  ControlledSelectField,
} from '../../shared';
import { PatientPrimaryPolicyholderInput } from '../../../../generated/graphql';
import { rules } from '../../demographics/rules';

export type PrimaryPolicyHolderFormValues = {
  relationToPatient: string | undefined;
  firstName: string;
  lastName: string;
  birthDate: string;
  addressStreet1: string;
  addressStreet2: string;
  addressCity: string;
  addressState: string;
  addressZipCode: string;
  customRelation: string;
  isPolicyholderAddressSameAsPatient: boolean;
};

const PrimaryPolicyHolder = () => {
  const dispatch = useAppDispatch();
  const { addToast } = useToasts();

  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);

  const patientGuardians = useSelector(selectPatientGuardians);
  const isGuardian = useSelector(selectIsGuardian);

  const secondaryGuardian = patientGuardians?.secondary;

  const prevPrimaryPolicyholder =
    medicalInfoAnswers.MI_PrimaryPolicyHolderDetails;

  const [isNotPrimaryPolicyHolder, setIsNotPrimaryPolicyHolder] = // for non-minor patients
    useState<boolean>(false);

  const [minorPolicyHolderIsNotGuardian, setMinorPolicyHolderIsNotGuardian] = // for minor patients
    useState<boolean>(false);

  const form = useForm<PrimaryPolicyHolderFormValues>({
    mode: 'onBlur',
    defaultValues: {
      relationToPatient: prevPrimaryPolicyholder?.relationToPatient ?? null,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
    trigger,
  } = form;

  const relationToPatient = watch('relationToPatient');

  const adultPatientIsNotPolicyHolder = isNotPrimaryPolicyHolder && !isGuardian;
  const minorPolicyHolderIsOther = minorPolicyHolderIsNotGuardian && isGuardian;

  const showPolicyHolderDetailsForm =
    adultPatientIsNotPolicyHolder || minorPolicyHolderIsOther;

  const onSubmit: SubmitHandler<PrimaryPolicyHolderFormValues> = async data => {
    try {
      const formatBirthDate = (date: string) =>
        format(new Date(date), 'yyyy-MM-dd');
      const formattedBirthDate = data.birthDate
        ? formatBirthDate(data.birthDate)
        : undefined;

      const dataToSend: PatientPrimaryPolicyholderInput =
        showPolicyHolderDetailsForm
          ? {
              firstName: data.firstName,
              lastName: data.lastName,
              birthDate: formattedBirthDate,
              relationToPatient: data.relationToPatient,
              isAddressLinkedWithPatient:
                data.isPolicyholderAddressSameAsPatient,
              addressStreet1: data.addressStreet1,
              addressStreet2: data.addressStreet2,
              addressCity: data.addressCity,
              addressState: data.addressState,
              addressZipCode: data.addressZipCode,
              ...(data.customRelation && {
                customRelation: data.customRelation,
              }),
            }
          : createPolicyholderObjectHelper(patientGuardians, relationToPatient);

      await dispatch(
        updatePrimaryPolicyholder(isNotPrimaryPolicyHolder, dataToSend)
      );
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  useEffect(() => {
    setIsNotPrimaryPolicyHolder(
      !(
        !relationToPatient ||
        relationToPatient === PolicyHolderRelationLabelValueMap.Me
      )
    );

    setMinorPolicyHolderIsNotGuardian(
      !(
        !relationToPatient ||
        relationToPatient !== MinorPolicyHolderRelationLabelValueMap.Other
      )
    );
  }, [relationToPatient]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <AssessmentCard>
        <ControlledSelectField<PrimaryPolicyHolderFormValues>
          control={control}
          name="relationToPatient"
          defaultValue=""
          label="Policyholder"
          options={
            isGuardian
              ? minorPolicyholderOptions(!!secondaryGuardian)
              : AdultPolicyholderOptions
          }
          rules={rules.policyholder}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Not sure?</AlertTitle>
          The policyholder is the person who owns your insurance plan. If you
          pay your plan’s premiums, you’re probably the policyholder. If you use
          a parent’s or partner’s plan, they are probably the policyholder. We
          need the policyholder’s information to check your insurance.
        </Alert>
      </AssessmentCard>
      <Collapse
        in={showPolicyHolderDetailsForm}
        unmountOnExit
        onExited={async () => trigger('relationToPatient')}
      >
        <PrimaryPolicyHolderForm form={form} />
      </Collapse>
      <AssessmentButtons
        primary={{
          type: 'submit',
          children: 'Continue',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default PrimaryPolicyHolder;
